import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  BarChart as RechartsBarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft,
  Briefcase,
  Users,
  CalendarIcon,
  Filter,
  FileText,
  Download,
  ArrowUpDown,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { useAnalyticsData } from "./AnalyticsData";
import { Skeleton, Box, Stack } from "@mui/material";
import {
  fetchMembersTab,
  fetchServicesTab,
  fetchAllReports,
  fetchReportById,
  getServiceDetails,
} from "@/dispatchers/analyticsDispatchers";
import AnalyticsSkeleton from "./AnalyticsSkeleton";

interface AnalyticsProps {
  isDarkMode?: boolean;
  initialTab?: "services" | "members" | "reports";
}

interface ServiceConsultation {
  id: string;
  patientName: string;
  practitioner: string;
  date: string;
  time: string;
  duration: number;
  compliance: number;
}


const formatDurationInMins = (seconds: number): string => {
  if (!seconds || isNaN(seconds) || seconds < 0) return "0.00";

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${mins}.${secs.toString().padStart(2, "0")}`;
};

const Analytics: React.FC<AnalyticsProps> = ({
  isDarkMode = false,
  initialTab = "services",
}) => {
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [selectedMemberData, setSelectedMemberData] = useState([]);
  const [activeTab, setActiveTab] = useState<
    "services" | "members" | "reports"
  >(initialTab);

  // Date filtering state
  const [filterType, setFilterType] = useState<"all" | "range" | "month">(
    "all",
  );
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString(),
  );
  const startYear = 2022;
  const currentYear = new Date().getFullYear();

  const yearOptions = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => (startYear + i).toString(),
  );

  // Sorting state for reports
  const [reportSortColumn, setReportSortColumn] = useState<string | null>(null);
  const [reportSortDirection, setReportSortDirection] = useState<
    "asc" | "desc"
  >("asc");

  // Sorting state for main services table
  const [mainServicesSortColumn, setMainServicesSortColumn] = useState<
    string | null
  >(null);
  const [mainServicesSortDirection, setMainServicesSortDirection] = useState<
    "asc" | "desc"
  >("asc");

  // Sorting state for services drill-down
  const [serviceSortColumn, setServiceSortColumn] = useState<string | null>(
    null,
  );
  const [serviceSortDirection, setServiceSortDirection] = useState<
    "asc" | "desc"
  >("asc");

  // Sorting state for members drill-down
  const [memberSortColumn, setMemberSortColumn] = useState<string | null>(null);
  const [memberSortDirection, setMemberSortDirection] = useState<
    "asc" | "desc"
  >("asc");
  const [serviceFilterPatient, setServiceFilterPatient] = useState("");
  const [serviceFilterPractitioner, setServiceFilterPractitioner] =
    useState("all");
  const [serviceFilterDate, setServiceFilterDate] = useState<Date | undefined>(
    undefined,
  );
  const [memberFilterPatient, setMemberFilterPatient] = useState("");
  const [memberFilterService, setMemberFilterService] = useState("all");
  const [memberFilterDate, setMemberFilterDate] = useState<Date | undefined>(
    undefined,
  );
  const [selectedMemberSatisfaction, setSelectedMemberSatisfaction] = useState(0);

  const getTrendChartDescription = () => {
    if (filterType === "range" && dateFrom && dateTo) {
      const diffInMonths = Math.floor(
        (dateTo.getTime() - dateFrom.getTime()) / (1000 * 60 * 60 * 24 * 30),
      );
      return diffInMonths <= 1 ? "Selected period" : `${diffInMonths} months`;
    } else if (filterType === "month" && selectedMonth && selectedYear) {
      const monthName = new Date(
        parseInt(selectedYear),
        parseInt(selectedMonth),
      ).toLocaleString("default", { month: "long" });
      return `${monthName} ${selectedYear}`;
    } else if (filterType === "all") {
      return "All time trend";
    } else {
      return "Last 6 months";
    }
  };
  const {
    revenueTrend,
    averageCompliance,
    servicesPerformance,
    bookingsGrowth,
    memberPerformance,
    membersCompliance, ///unused
    performanceDistribution,
    isAnalyticsLoading,
    companyId,
    allAnalyticsReports,
    currentPharmacyId,
    selectedReportData,
    serviceDetails,
    handleSetSelectedService,
    isServiceDetailsLoading,
    totalRevenue,
    currentCompanyId,
    handleSetSelectedMember,
    isMemberDetailsLoading,
    totalMemberRevenue,
    handleSelectReport,
    memberSpecificAvgDuration,
    serviceAvg
  } = useAnalyticsData();

  const getDateRange = () => {
    let startDate: Date;
    let endDate: Date;

    if (filterType === "range" && dateFrom && dateTo) {
      startDate = new Date(dateFrom);
      endDate = new Date(dateTo);
      endDate.setHours(23, 59, 59, 999);
    } else if (filterType === "month" && selectedMonth && selectedYear) {
      const year = parseInt(selectedYear);
      const month = parseInt(selectedMonth);

      startDate = new Date(year, month, 1);
      endDate = new Date(year, month + 1, 0);
      endDate.setHours(23, 59, 59, 999);
    } else if (filterType === "all") {
      startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 5);
      startDate.setMonth(0, 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
    } else {
      endDate = new Date();
      endDate.setHours(23, 59, 59, 999);

      startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
    }

    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();

    return { startDate, endDate, formattedStartDate, formattedEndDate };
  };

  
    const setHandleSelectReport = (reportTypeId: string) => {
    const { formattedStartDate, formattedEndDate } = getDateRange();
    try {
      handleSelectReport(reportTypeId,formattedStartDate,formattedEndDate);
      setSelectedReport(reportTypeId);
    } catch (ex) {
      console.log(ex);
    }
};

  const handleRefresh = async () => {
    if (isAnalyticsLoading) return;
    const { formattedStartDate, formattedEndDate } = getDateRange();
    const selectedCompanyId = currentCompanyId;
    if (activeTab === "services") {
      await fetchServicesTab({
        companyId: selectedCompanyId !== "" ? selectedCompanyId : companyId,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    } else if (activeTab === "members") {
      await fetchMembersTab({
        companyId: selectedCompanyId !== "" ? selectedCompanyId : companyId,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    } else if (activeTab === "reports") {
      await fetchAllReports({
        companyID: selectedCompanyId !== "" ? selectedCompanyId : companyId,
        pharmacyID: currentPharmacyId,
      });
    }
  };

  const handleTabClick = async (serviceName) => {
    setActiveTab(serviceName);
    const { formattedStartDate, formattedEndDate } = getDateRange();
    const selectedCompanyId = currentCompanyId;
    if (serviceName === "services") {
      await fetchServicesTab({
        companyId: selectedCompanyId !== "" ? selectedCompanyId : companyId,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    } else if (serviceName === "members") {
      await fetchMembersTab({
        companyId: selectedCompanyId !== "" ? selectedCompanyId : companyId,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    } else if (serviceName === "reports") {
      await fetchAllReports({
        companyID: selectedCompanyId !== "" ? selectedCompanyId : companyId,
        pharmacyID: currentPharmacyId,
      });
    }
  };
  // Sample data for Services analytics

  // Handler for sorting main services table
  const handleMainServicesSort = (column: string) => {
    if (mainServicesSortColumn === column) {
      setMainServicesSortDirection(
        mainServicesSortDirection === "asc" ? "desc" : "asc",
      );
    } else {
      setMainServicesSortColumn(column);
      setMainServicesSortDirection("asc");
    }
  };

  const cardBgClass = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white";
  const textClass = isDarkMode ? "text-gray-100" : "text-gray-900";
  const mutedTextClass = isDarkMode ? "text-gray-400" : "text-gray-600";
  const labelClass = isDarkMode ? "text-gray-300" : "text-gray-700";
  const rowHoverClass = isDarkMode
    ? "hover:bg-gray-700/50"
    : "hover:bg-gray-50";
  const tableRowClass = isDarkMode ? "border-gray-700" : "border-gray-200";

  // CSV Export utility function
  const exportToCSV = (reportId: string, reportName: string) => {
    // Use selectedReportData from API instead of static data
    const data = selectedReportData || [];
    if (!data || data.length === 0) {
      console.warn("No data available to export");
      return;
    }

    // Get headers from the first object's keys, filtering out _id
    const allHeaders = Object.keys(data[0]);
    const headers = allHeaders.filter((header) => header !== "_id");

    // Create CSV content
    const csvContent = [
      headers.join(","), // Header row
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            // Handle null/undefined
            if (value === null || value === undefined) {
              return "";
            }
            // Convert value to string
            const stringValue = String(value);
            // Escape values that contain commas, quotes, or newlines
            if (
              stringValue.includes(",") ||
              stringValue.includes('"') ||
              stringValue.includes("\n")
            ) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(","),
      ),
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${reportName.replace(/\s+/g, "_")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // const selectedServiceData = selectedService
  //   ? filteredConsultationsByService[selectedService]
  //   : null;
  const [selectedServiceData, setSelectedServiceData] = useState<
    ServiceConsultation[]
  >([]);

 
  if (isServiceDetailsLoading) return <AnalyticsSkeleton />;
  if (isMemberDetailsLoading) return <AnalyticsSkeleton />;

  if (selectedService && selectedServiceData) {

    const filteredServiceData = selectedServiceData.filter((c) => {
      if (
        serviceFilterPatient &&
        !c.patientName
          .toLowerCase()
          .includes(serviceFilterPatient.toLowerCase())
      )
        return false;
      if (
        serviceFilterPractitioner !== "all" &&
        c.practitioner !== serviceFilterPractitioner
      )
        return false;
      if (serviceFilterDate) {
        const cDate = new Date(c.date).toDateString();
        if (cDate !== serviceFilterDate.toDateString()) return false;
      }
      return true;
    });

  const avgDuration = serviceAvg;

    const avgCompliance =
  filteredServiceData.length === 0
    ? 0
    : (
        filteredServiceData.reduce((sum, c) => sum + c.compliance, 0) /
        filteredServiceData.length
      ).toFixed(2);
    const totalConsultations = filteredServiceData.length;

    // Sort services data
    const sortedServiceData = [...filteredServiceData].sort((a, b) => {
      if (!serviceSortColumn) return 0;

      let aVal: any = a[serviceSortColumn as keyof ServiceConsultation];
      let bVal: any = b[serviceSortColumn as keyof ServiceConsultation];

      // Handle date sorting
      if (serviceSortColumn === "date") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      // Handle numeric sorting
      if (typeof aVal === "number" && typeof bVal === "number") {
        return serviceSortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Handle string sorting
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (serviceSortDirection === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });

    const handleServiceSort = (column: string) => {
      if (serviceSortColumn === column) {
        setServiceSortDirection(
          serviceSortDirection === "asc" ? "desc" : "asc",
        );
      } else {
        setServiceSortColumn(column);
        setServiceSortDirection("asc");
      }
    };

    return (
      <div
        className={`flex-1 flex flex-col overflow-hidden ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        {/* Header with back button */}
        <div
          className={`px-6 py-4 border-b ${
            isDarkMode
              ? "border-gray-700 bg-gray-800"
              : "border-gray-200 bg-white"
          }`}
        >
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedService(null);
              setServiceSortColumn(null);
              setServiceSortDirection("asc");
              setServiceFilterPatient("");
              setServiceFilterPractitioner("all");
              setServiceFilterDate(undefined);
            }}
            className={`mb-4 ${
              isDarkMode
                ? "text-gray-300 hover:text-white hover:bg-gray-700"
                : ""
            }`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Analytics
          </Button>
          <h1 className={`text-2xl font-bold ${textClass}`}>
            {selectedService}
          </h1>
        </div>

        {/* Stats Overview */}
        <div className="px-6 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Total Consultations
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {totalConsultations}
                </div>
              </CardContent>
            </Card>

            <Card
              className={`${cardBgClass} border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Total Revenue
                </div>
                <div className="text-2xl font-bold mt-2 text-green-500">
                  £{totalRevenue}
                </div>
              </CardContent>
            </Card>

            {/* <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Avg Compliance
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {selectedServiceInfo?.compliance}%
                </div>
              </CardContent>
            </Card> */}

            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Avg Transcription Duration (Mins)
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {avgDuration.toFixed(2)}
                </div>
              </CardContent>
            </Card>

            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Avg Compliance
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {avgCompliance}%
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Consultation List */}
        <div className="flex-1 overflow-y-auto p-6">
          <Card
            className={`${cardBgClass} border ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Consultations
                  </CardTitle>
                  <CardDescription className={mutedTextClass}>
                    All consultations for this service
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Patient Name Search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search patient..."
                      value={serviceFilterPatient}
                      onChange={(e) => setServiceFilterPatient(e.target.value)}
                      className={`h-9 w-[160px] rounded-md border px-3 text-xs ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400"
                          : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                      } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    />
                  </div>

                  {/* Practitioner Dropdown */}
                  <Select
                    value={serviceFilterPractitioner}
                    onValueChange={setServiceFilterPractitioner}
                  >
                    <SelectTrigger
                      className={`w-[160px] h-9 text-xs ${isDarkMode ? "bg-gray-700 border-gray-600" : ""}`}
                    >
                      <SelectValue placeholder="Practitioner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Practitioners</SelectItem>
                      {[
                        ...new Set(
                          selectedServiceData.map((c) => c.practitioner),
                        ),
                      ].map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Date Filter */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`h-9 w-[140px] justify-start text-left text-xs font-normal ${
                          !serviceFilterDate && "text-muted-foreground"
                        } ${isDarkMode ? "bg-gray-700 border-gray-600 hover:bg-gray-600" : ""}`}
                      >
                        <CalendarIcon className="mr-2 h-3 w-3" />
                        {serviceFilterDate
                          ? format(serviceFilterDate, "PP")
                          : "Filter date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={serviceFilterDate}
                        onSelect={setServiceFilterDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Clear Filters */}
                  {(serviceFilterPatient ||
                    serviceFilterPractitioner !== "all" ||
                    serviceFilterDate) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-9 text-xs ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500"}`}
                      onClick={() => {
                        setServiceFilterPatient("");
                        setServiceFilterPractitioner("all");
                        setServiceFilterDate(undefined);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${tableRowClass}`}>
                      <th
                        onClick={() => handleServiceSort("patientName")}
                        className={`text-left py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center gap-2">
                          Patient Name
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleServiceSort("practitioner")}
                        className={`text-left py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center gap-2">
                          Practitioner
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleServiceSort("date")}
                        className={`text-center py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Date
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleServiceSort("time")}
                        className={`text-center py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Time
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleServiceSort("duration")}
                        className={`text-center py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Transcription Duration (Mins)
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleServiceSort("compliance")}
                        className={`text-right py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Compliance Rating
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedServiceData.map((consultation) => (
                      <tr
                        key={consultation.id}
                        className={`border-b ${tableRowClass} ${rowHoverClass}`}
                      >
                        <td className={`py-3 px-4 ${textClass}`}>
                          {consultation.patientName}
                        </td>
                        <td className={`py-3 px-4 ${textClass}`}>
                          {consultation.practitioner}
                        </td>
                        <td
                          className={`text-center py-3 px-4 ${mutedTextClass}`}
                        >
                          {new Date(consultation.date).toLocaleDateString(
                            "en-GB",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </td>
                        <td
                          className={`text-center py-3 px-4 ${mutedTextClass}`}
                        >
                          {consultation.time}
                        </td>
                        <td
                          className={`text-center py-3 px-4 ${mutedTextClass}`}
                        >
                          {formatDurationInMins(consultation.duration)}
                        </td>
                        <td className={`text-right py-3 px-4 font-medium`}>
                          <span
                            className={`text-sm ${
                              consultation.compliance >= 94
                                ? "text-green-500"
                                : consultation.compliance >= 90
                                  ? "text-yellow-500"
                                  : "text-orange-500"
                            }`}
                          >
                                {consultation.compliance != null
                        ? `${Number(consultation.compliance).toFixed(2)}%`
                        : "0.00%"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (selectedMember && selectedMemberData) {
    const filteredMemberData = selectedMemberData.filter((c) => {
      if (
        memberFilterPatient &&
        !c.patientName.toLowerCase().includes(memberFilterPatient.toLowerCase())
      )
        return false;
      if (
        memberFilterService !== "all" &&
        c.serviceName !== memberFilterService
      )
        return false;
      if (memberFilterDate) {
        const cDate = new Date(c.date).toDateString();
        if (cDate !== memberFilterDate.toDateString()) return false;
      }
      return true;
    });
    
    const avgDuration =
      filteredMemberData.length === 0
        ? 0
        : (
            filteredMemberData.reduce((sum, c) => sum + c.duration, 0) /
            filteredMemberData.length
          ).toFixed(0);
   const avgCompliance =
  filteredMemberData.length === 0
    ? 0
    : (
        filteredMemberData.reduce(
          (sum, c) => sum + Number(c.compliance || 0),
          0
        ) / filteredMemberData.length
      ).toFixed(2);
          
    const totalPatients = new Set(filteredMemberData.map((c) => c.patientName))
      .size;
    const totalConsultations = filteredMemberData.length;
    // Sort members data
    const sortedMemberData = [...filteredMemberData].sort((a, b) => {
      if (!memberSortColumn) return 0;

      let aVal: any = a[memberSortColumn as keyof ServiceConsultation];
      let bVal: any = b[memberSortColumn as keyof ServiceConsultation];

      // Handle date sorting
      if (memberSortColumn === "date") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      // Handle numeric sorting
      if (typeof aVal === "number" && typeof bVal === "number") {
        return memberSortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Handle string sorting
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (memberSortDirection === "asc") {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });

    const handleMemberSort = (column: string) => {
      if (memberSortColumn === column) {
        setMemberSortDirection(memberSortDirection === "asc" ? "desc" : "asc");
      } else {
        setMemberSortColumn(column);
        setMemberSortDirection("asc");
      }
    };

    return (
      <div
        className={`flex-1 flex flex-col overflow-hidden ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        {/* Header with back button */}
        <div
          className={`px-6 py-4 border-b ${
            isDarkMode
              ? "border-gray-700 bg-gray-800"
              : "border-gray-200 bg-white"
          }`}
        >
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedMember(null);
              setMemberSortColumn(null);
              setMemberSortDirection("asc");
              setMemberFilterPatient("");
              setMemberFilterService("all");
              setMemberFilterDate(undefined);
            }}
            className={`mb-4 ${
              isDarkMode
                ? "text-gray-300 hover:text-white hover:bg-gray-700"
                : ""
            }`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Analytics
          </Button>
          <h1 className={`text-2xl font-bold ${textClass}`}>
            {selectedMember}
          </h1>
        </div>

        {/* Stats Overview */}
        <div className="px-6 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Total Consultations
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {totalConsultations}
                </div>
              </CardContent>
            </Card>

            {/* <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Unique Patients
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {totalPatients}
                </div>
              </CardContent>
            </Card> 

             <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Satisfaction Score
                </div>
                <div className={`text-2xl font-bold mt-2 text-green-500`}>
                  {selectedMemberSatisfaction}%

                </div>
              </CardContent>
            </Card> */}

            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Avg Transcription Duration (Mins)
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {memberSpecificAvgDuration.toFixed(2)}
                </div>
              </CardContent>
            </Card>

            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardContent className="pt-6">
                <div className={`text-sm font-medium ${mutedTextClass}`}>
                  Avg Compliance
                </div>
                <div className={`text-2xl font-bold mt-2 ${textClass}`}>
                  {avgCompliance}%
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Consultation List */}
        <div className="flex-1 overflow-y-auto p-6">
          <Card
            className={`${cardBgClass} border ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Consultations
                  </CardTitle>
                  <CardDescription className={mutedTextClass}>
                    All consultations completed by this member
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Patient Name Search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search patient..."
                      value={memberFilterPatient}
                      onChange={(e) => setMemberFilterPatient(e.target.value)}
                      className={`h-9 w-[160px] rounded-md border px-3 text-xs ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400"
                          : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                      } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    />
                  </div>

                  {/* Service Dropdown */}
                  <Select
                    value={memberFilterService}
                    onValueChange={setMemberFilterService}
                  >
                    <SelectTrigger
                      className={`w-[160px] h-9 text-xs ${isDarkMode ? "bg-gray-700 border-gray-600" : ""}`}
                    >
                      <SelectValue placeholder="Service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      {[
                        ...new Set(
                          selectedMemberData
                            .map((c) => c.serviceName)
                            .filter(Boolean),
                        ),
                      ].map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Date Filter */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`h-9 w-[140px] justify-start text-left text-xs font-normal ${
                          !memberFilterDate && "text-muted-foreground"
                        } ${isDarkMode ? "bg-gray-700 border-gray-600 hover:bg-gray-600" : ""}`}
                      >
                        <CalendarIcon className="mr-2 h-3 w-3" />
                        {memberFilterDate
                          ? format(memberFilterDate, "PP")
                          : "Filter date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <Calendar
                        mode="single"
                        selected={memberFilterDate}
                        onSelect={setMemberFilterDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Clear Filters */}
                  {(memberFilterPatient ||
                    memberFilterService !== "all" ||
                    memberFilterDate) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-9 text-xs ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500"}`}
                      onClick={() => {
                        setMemberFilterPatient("");
                        setMemberFilterService("all");
                        setMemberFilterDate(undefined);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${tableRowClass}`}>
                      <th
                        onClick={() => handleMemberSort("patientName")}
                        className={`text-left py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center gap-2">
                          Patient Name
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        className={`text-left py-3 px-4 font-medium ${labelClass}`}
                      >
                        Service
                      </th>
                      <th
                        onClick={() => handleMemberSort("date")}
                        className={`text-center py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Date
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleMemberSort("time")}
                        className={`text-center py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Time
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleMemberSort("duration")}
                        className={`text-center py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Transcription Duration (Mins)
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleMemberSort("compliance")}
                        className={`text-right py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                          isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                        } transition-colors`}
                      >
                        <div className="flex items-center justify-end gap-2">
                          Compliance Rating
                          <ArrowUpDown className="w-3 h-3 opacity-50" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMemberData.map((consultation) => (
                      <tr
                        key={consultation.id}
                        className={`border-b ${tableRowClass} ${rowHoverClass}`}
                      >
                        <td className={`py-3 px-4 ${textClass}`}>
                          {consultation.patientName}
                        </td>
                        <td className={`py-3 px-4 ${textClass}`}>
                          {consultation.serviceName}
                        </td>
                        <td
                          className={`text-center py-3 px-4 ${mutedTextClass}`}
                        >
                          {new Date(consultation.date).toLocaleDateString(
                            "en-GB",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            },
                          )}
                        </td>
                        <td
                          className={`text-center py-3 px-4 ${mutedTextClass}`}
                        >
                          {consultation.time?.trim() ? consultation.time : "-"}
                        </td>
                        <td
                          className={`text-center py-3 px-4 ${mutedTextClass}`}
                        >
                          {formatDurationInMins(consultation.duration)}
                        </td>
                        <td className={`text-right py-3 px-4 font-medium`}>
                          <span
                            className={`text-sm ${
                              consultation.compliance >= 94
                                ? "text-green-500"
                                : consultation.compliance >= 90
                                  ? "text-yellow-500"
                                  : "text-orange-500"
                            }`}
                          >
                            {consultation.compliance != null
                        ? `${Number(consultation.compliance).toFixed(2)}%`
                        : "0.00%"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const renderCellValue = (value: any): React.ReactNode => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return "-";
    }

    // Handle objects
    if (typeof value === "object") {
      // If it's an array, join the values
      if (Array.isArray(value)) {
        return value.join(", ");
      }

      // If it's an object, convert to JSON string
      return JSON.stringify(value);
    }

    // Handle boolean
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    // Handle dates
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }

    // Return primitive values as-is
    return String(value);
  };

  return (
    <div
      className={`flex-1 flex flex-col overflow-hidden ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      {/* Custom Tab Bar matching main navigation */}
      <div
        className={`px-4 py-4 border-b transition-colors duration-300 overflow-visible ${
          isDarkMode
            ? "border-gray-700 bg-gray-800"
            : "border-gray-200 border-b-gray-100 bg-white"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0">
            {/* Services Tab */}
            <button
              onClick={() => handleTabClick("services")}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                activeTab === "services"
                  ? isDarkMode
                    ? "text-white"
                    : "text-gray-900"
                  : isDarkMode
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Services
              {activeTab === "services" && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-black rounded-full" />
              )}
            </button>

            {/* Members Tab */}
            <button
              onClick={() => handleTabClick("members")}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                activeTab === "members"
                  ? isDarkMode
                    ? "text-white"
                    : "text-gray-900"
                  : isDarkMode
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Users className="w-4 h-4" />
              Members
              {activeTab === "members" && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-black rounded-full" />
              )}
            </button>

            {/* Reports Tab */}
            <button
              onClick={() => handleTabClick("reports")}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                activeTab === "reports"
                  ? isDarkMode
                    ? "text-white"
                    : "text-gray-900"
                  : isDarkMode
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FileText className="w-4 h-4" />
              Reports
              {activeTab === "reports" && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-black rounded-full" />
              )}
            </button>
          </div>

          {/* Date Filter Controls */}
          <div className="flex items-center gap-3">
            <Filter className={`w-4 h-4 ${mutedTextClass}`} />

            {/* Filter Type Selector */}
            <Select
              value={filterType}
              onValueChange={(value: any) => setFilterType(value)}
            >
              <SelectTrigger
                className={`w-[140px] ${
                  isDarkMode ? "bg-gray-700 border-gray-600" : ""
                }`}
              >
                <SelectValue placeholder="Filter by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="range">Date Range</SelectItem>
                <SelectItem value="month">Month</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Range Pickers */}
            {filterType === "range" && (
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[140px] justify-start text-left font-normal ${
                        !dateFrom && "text-muted-foreground"
                      } ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                          : ""
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, "PP") : "From date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={setDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-[140px] justify-start text-left font-normal ${
                        !dateTo && "text-muted-foreground"
                      } ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                          : ""
                      }`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, "PP") : "To date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={setDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </>
            )}

            {/* Month and Year Selectors */}
            {filterType === "month" && (
              <>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger
                    className={`w-[130px] ${
                      isDarkMode ? "bg-gray-700 border-gray-600" : ""
                    }`}
                  >
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">January</SelectItem>
                    <SelectItem value="1">February</SelectItem>
                    <SelectItem value="2">March</SelectItem>
                    <SelectItem value="3">April</SelectItem>
                    <SelectItem value="4">May</SelectItem>
                    <SelectItem value="5">June</SelectItem>
                    <SelectItem value="6">July</SelectItem>
                    <SelectItem value="7">August</SelectItem>
                    <SelectItem value="8">September</SelectItem>
                    <SelectItem value="9">October</SelectItem>
                    <SelectItem value="10">November</SelectItem>
                    <SelectItem value="11">December</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger
                    className={`w-[100px] ${
                      isDarkMode ? "bg-gray-700 border-gray-600" : ""
                    }`}
                  >
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {/* Services Tab Content */}
        {/* Services Tab Content */}
        {activeTab === "services" && (
          <div className="p-6 space-y-6">
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={isAnalyticsLoading}
                className={`${
                  isDarkMode ? "border-slate-800 text-slate-400" : ""
                } ${isAnalyticsLoading ? "opacity-50" : ""}`}
                title="Refresh data"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    isAnalyticsLoading ? "animate-spin" : ""
                  }`}
                />
              </Button>
            </div>

            {/* Key Analytics - 3 Graphs in a row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Total Revenue Trend */}
              <Card
                className={`${cardBgClass} border ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <CardHeader>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Total Revenue Trend
                  </CardTitle>
                  <CardDescription className={`text-xs ${mutedTextClass}`}>
                    {getTrendChartDescription()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isAnalyticsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={320}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "8px",
                        }}
                      />
                    </Stack>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart
                        data={revenueTrend}
                        margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="month"
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <YAxis
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${
                              isDarkMode ? "#374151" : "#e5e7eb"
                            }`,
                          }}
                          labelStyle={{
                            color: isDarkMode ? "#f3f4f6" : "#111827",
                          }}
                          formatter={(value: number) =>
                            `£${value.toLocaleString()}`
                          }
                        />
                        <Line
                          type="monotone"
                          dataKey="revenue"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Average Service Rating Trend */}
              <Card
                className={`${cardBgClass} border ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <CardHeader>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Average Service Compliance
                  </CardTitle>
                  <CardDescription className={`text-xs ${mutedTextClass}`}>
                    {getTrendChartDescription()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isAnalyticsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={320}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "8px",
                        }}
                      />
                    </Stack>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart
                        data={averageCompliance}
                        margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="month"
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <YAxis
                          domain={[85, 100]}
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${
                              isDarkMode ? "#374151" : "#e5e7eb"
                            }`,
                          }}
                          labelStyle={{
                            color: isDarkMode ? "#f3f4f6" : "#111827",
                          }}
                          formatter={(value: number) => `${value}%`}
                        />
                        <Line
                          type="monotone"
                          dataKey="compliance"
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Total Bookings Growth */}
              <Card
                className={`${cardBgClass} border ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <CardHeader>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Total Bookings Growth
                  </CardTitle>
                  <CardDescription className={`text-xs ${mutedTextClass}`}>
                    {getTrendChartDescription()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isAnalyticsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={320}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "8px",
                        }}
                      />
                    </Stack>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <RechartsBarChart
                        data={bookingsGrowth}
                        margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="month"
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <YAxis
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${
                              isDarkMode ? "#374151" : "#e5e7eb"
                            }`,
                          }}
                          labelStyle={{
                            color: isDarkMode ? "#f3f4f6" : "#111827",
                          }}
                        />
                        <Bar dataKey="bookings" fill="#8b5cf6" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Services Performance Table */}
            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardHeader>
                <CardTitle className={`text-sm font-semibold ${textClass}`}>
                  Services Performance
                </CardTitle>
                <CardDescription className={mutedTextClass}>
                  Click column headers to sort, click services to view details
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isAnalyticsLoading ? (
                  <Stack spacing={1.5}>
                    {[...Array(6)].map((_, idx) => (
                      <Skeleton
                        key={idx}
                        variant="rectangular"
                        width="100%"
                        height={52}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "4px",
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${tableRowClass}`}>
                          <th
                            onClick={() => handleMainServicesSort("name")}
                            className={`text-left py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                              isDarkMode
                                ? "hover:bg-gray-700"
                                : "hover:bg-gray-100"
                            } transition-colors`}
                          >
                            <div className="flex items-center gap-2">
                              Service Name
                              <ArrowUpDown className="w-3.5 h-3.5" />
                            </div>
                          </th>
                          <th
                            className={`text-left py-3 px-4 font-medium ${labelClass}`}
                          >
                            Bookings Volume
                          </th>
                          <th
                            onClick={() => handleMainServicesSort("bookings")}
                            className={`text-center py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                              isDarkMode
                                ? "hover:bg-gray-700"
                                : "hover:bg-gray-100"
                            } transition-colors`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              Bookings
                              <ArrowUpDown className="w-3.5 h-3.5" />
                            </div>
                          </th>
                          <th
                            onClick={() => handleMainServicesSort("revenue")}
                            className={`text-right py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                              isDarkMode
                                ? "hover:bg-gray-700"
                                : "hover:bg-gray-100"
                            } transition-colors`}
                          >
                            <div className="flex items-center justify-end gap-2">
                              Revenue
                              <ArrowUpDown className="w-3.5 h-3.5" />
                            </div>
                          </th>
                          {/* <th
                            onClick={() => handleMainServicesSort("compliance")}
                            className={`text-right py-3 px-4 font-medium ${labelClass} cursor-pointer hover:bg-opacity-50 ${
                              isDarkMode
                                ? "hover:bg-gray-700"
                                : "hover:bg-gray-100"
                            } transition-colors`}
                          >
                            <div className="flex items-center justify-end gap-2">
                              Compliance
                              <ArrowUpDown className="w-3.5 h-3.5" />
                            </div>
                          </th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {servicesPerformance.map((service, idx) => {
                          const maxBookings = Math.max(
                            ...servicesPerformance.map((s) => s.bookings),
                          );
                          const bookingPercentage =
                            (service.bookings / maxBookings) * 100;

                          return (
                            <tr
                              key={idx}
                              onClick={async () => {
                            const { formattedStartDate, formattedEndDate } = getDateRange();
                                const serviceDetails =
                                  await handleSetSelectedService(service.name,formattedStartDate,formattedEndDate);
                                setSelectedService(service.name);
                                setSelectedServiceData(serviceDetails);
                              }}
                              className={`border-b ${tableRowClass} ${rowHoverClass} cursor-pointer transition-colors`}
                            >
                              <td
                                className={`py-3 px-4 ${textClass} font-medium`}
                              >
                                {service.name}
                              </td>
                              <td className={`py-3 px-4`}>
                                <div className="w-full max-w-[200px]">
                                  <div
                                    className={`h-2 rounded-full ${
                                      isDarkMode ? "bg-gray-700" : "bg-gray-200"
                                    }`}
                                  >
                                    <div
                                      className="h-2 rounded-full bg-blue-500"
                                      style={{ width: `${bookingPercentage}%` }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td
                                className={`text-center py-3 px-4 ${mutedTextClass}`}
                              >
                                {service.bookings}
                              </td>
                              <td
                                className={`text-right py-3 px-4 font-medium text-green-500`}
                              >
                                £{service.revenue.toLocaleString()}
                              </td>
                              {/* <td
                                className={`text-right py-3 px-4 ${mutedTextClass}`}
                              >
                                {service.compliance}%
                              </td> */}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Members Tab Content */}
        {/* Members Tab Content */}
        {activeTab === "members" && (
          <div className="p-6 space-y-6">
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={isAnalyticsLoading}
                className={`${
                  isDarkMode ? "border-slate-800 text-slate-400" : ""
                } ${isAnalyticsLoading ? "opacity-50" : ""}`}
                title="Refresh data"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    isAnalyticsLoading ? "animate-spin" : ""
                  }`}
                />
              </Button>
            </div>
            {/* Key Analytics - 3 Graphs in a row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Team Members Performance */}
              <Card
                className={`${cardBgClass} border ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <CardHeader>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Team Members Performance
                  </CardTitle>
                  <CardDescription className={`text-xs ${mutedTextClass}`}>
                    Consultations by member
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isAnalyticsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={320}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "8px",
                        }}
                      />
                    </Stack>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <RechartsBarChart
                        data={memberPerformance}
                        margin={{ top: 5, right: 10, left: -20, bottom: 60 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="name"
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "10px" }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${
                              isDarkMode ? "#374151" : "#e5e7eb"
                            }`,
                          }}
                          labelStyle={{
                            color: isDarkMode ? "#f3f4f6" : "#111827",
                          }}
                        />
                        <Bar dataKey="consultations" fill="#10b981" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Average Compliance Rating Trend */}
              <Card
                className={`${cardBgClass} border ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <CardHeader>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Average Compliance
                  </CardTitle>
                  <CardDescription className={`text-xs ${mutedTextClass}`}>
                    {getTrendChartDescription()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isAnalyticsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={320}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "8px",
                        }}
                      />
                    </Stack>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart
                        data={averageCompliance}
                        margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="month"
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <YAxis
                          domain={[85, 100]}
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${
                              isDarkMode ? "#374151" : "#e5e7eb"
                            }`,
                          }}
                          labelStyle={{
                            color: isDarkMode ? "#f3f4f6" : "#111827",
                          }}
                          formatter={(value: number) => `${value}%`}
                        />
                        <Line
                          type="monotone"
                          dataKey="compliance"
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Active Members Performance Distribution */}
              <Card
                className={`${cardBgClass} border ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <CardHeader>
                  <CardTitle className={`text-sm font-semibold ${textClass}`}>
                    Performance Distribution
                  </CardTitle>
                  <CardDescription className={`text-xs ${mutedTextClass}`}>
                    By consultation count
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isAnalyticsLoading ? (
                    <Stack spacing={2}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={320}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "8px",
                        }}
                      />
                    </Stack>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <RechartsBarChart
                        data={performanceDistribution}
                        margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={isDarkMode ? "#374151" : "#e5e7eb"}
                        />
                        <XAxis
                          dataKey="range"
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <YAxis
                          stroke={isDarkMode ? "#9ca3af" : "#6b7280"}
                          style={{ fontSize: "12px" }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                            border: `1px solid ${
                              isDarkMode ? "#374151" : "#e5e7eb"
                            }`,
                          }}
                          labelStyle={{
                            color: isDarkMode ? "#f3f4f6" : "#111827",
                          }}
                          formatter={(value: number) => `${value} members`}
                        />
                        <Bar dataKey="members" fill="#f59e0b" />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Members Stats Table */}
            <Card
              className={`${cardBgClass} border ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <CardHeader>
                <CardTitle className={`text-sm font-semibold ${textClass}`}>
                  Member Statistics
                </CardTitle>
                <CardDescription className={mutedTextClass}>
                  Click on a member to view consultation details
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isAnalyticsLoading ? (
                  <Stack spacing={1.5}>
                    {[...Array(5)].map((_, idx) => (
                      <Skeleton
                        key={idx}
                        variant="rectangular"
                        width="100%"
                        height={52}
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.06)",
                          borderRadius: "4px",
                        }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${tableRowClass}`}>
                          <th
                            className={`text-left py-3 px-4 font-medium ${labelClass}`}
                          >
                            Member Name
                          </th>
                          <th
                            className={`text-center py-3 px-4 font-medium ${labelClass}`}
                          >
                            Total Consultations
                          </th>
                          <th
                            className={`text-center py-3 px-4 font-medium ${labelClass}`}
                          >
                            Avg Transcription Duration (Mins)
                          </th>
                          {/* <th
                            className={`text-right py-3 px-4 font-medium ${labelClass}`}
                          >
                            Satisfaction
                          </th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {memberPerformance.map((member, idx) => (
                          <tr
                            key={idx}
                            onClick={async () => {
                            const { formattedStartDate, formattedEndDate } = getDateRange();
                              const memberData = await handleSetSelectedMember(
                                member.email,formattedStartDate,formattedEndDate
                              );
                              setSelectedMember(member.name);
                              setSelectedMemberData(memberData);
                                setSelectedMemberSatisfaction(member.satisfaction);

                            }}
                            className={`border-b ${tableRowClass} ${rowHoverClass} cursor-pointer transition-colors`}
                          >
                            <td
                              className={`py-3 px-4 ${textClass} font-medium`}
                            >
                              {member.name}
                            </td>
                            <td
                              className={`text-center py-3 px-4 ${mutedTextClass}`}
                            >
                              {member.consultations}
                            </td>
                            <td
                              className={`text-center py-3 px-4 ${mutedTextClass}`}
                            >
                              {Number(member.avgDuration).toFixed(2)}
                            </td>
                            {/* <td className={`text-right py-3 px-4 font-medium`}>
                              <span className="text-green-500">
                                {member.satisfaction}%
                              </span>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reports Tab Content */}
        {activeTab === "reports" && !selectedReport && (
          <div className="p-6">
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefresh}
                disabled={isAnalyticsLoading}
                className={`${
                  isDarkMode ? "border-slate-800 text-slate-400" : ""
                } ${isAnalyticsLoading ? "opacity-50" : ""}`}
                title="Refresh data"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    isAnalyticsLoading ? "animate-spin" : ""
                  }`}
                />
              </Button>
            </div>
            <div className="mb-6">
              <h2 className={`text-xl font-bold ${textClass}`}>
                Available Reports
              </h2>
              <p className={`text-sm mt-1 ${mutedTextClass}`}>
                Select a report to view detailed data and export to CSV
              </p>
            </div>

            {isAnalyticsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(8)].map((_, idx) => (
                  <Skeleton
                    key={idx}
                    variant="rectangular"
                    width="100%"
                    height={180}
                    sx={{
                      bgcolor: isDarkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.06)",
                      borderRadius: "8px",
                    }}
                  />
                ))}
              </div>
            ) : allAnalyticsReports && allAnalyticsReports.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allAnalyticsReports.map((report) => (
                  <Card
                    key={report._id}
                    className={`${cardBgClass} border ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    } cursor-pointer transition-all hover:shadow-md ${rowHoverClass}`}
                    onClick={() => setHandleSelectReport(report._id)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <FileText className={`w-5 h-5 ${mutedTextClass}`} />
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            report.tag === "Finance"
                              ? "bg-green-100 text-green-700"
                              : report.tag === "Appointments and Services"
                                ? "bg-blue-100 text-blue-700"
                                : report.tag === "Productivity"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-orange-100 text-orange-700"
                          }`}
                        >
                          {report.tag}
                        </span>
                      </div>
                      <CardTitle
                        className={`text-sm font-semibold mt-2 ${textClass}`}
                      >
                        {report.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className={`text-sm ${mutedTextClass}`}>
                        {report.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div
                className={`h-full flex items-center justify-center min-h-96 rounded-lg border-2 border-dashed ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-800/50"
                    : "border-gray-300 bg-gray-50"
                }`}
              >
                <div className="text-center">
                  <FileText
                    className={`w-16 h-16 mx-auto mb-4 ${mutedTextClass}`}
                  />
                  <h3 className={`text-lg font-semibold mb-2 ${textClass}`}>
                    No Reports Available
                  </h3>
                  <p className={`text-sm ${mutedTextClass}`}>
                    There are no reports to display at the moment.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Report Detail View */}
        {activeTab === "reports" &&
          selectedReport &&
          (() => {
            // Use selectedReportData from the API instead of static data
            const reportData = selectedReportData || [];
            const allHeaders =
              reportData.length > 0 ? Object.keys(reportData[0]) : [];
            // const headers =
            //   reportData.length > 0 ? Object.keys(reportData[0]) : [];

            const headers = allHeaders.filter((header) => header !== "_id");
            const selectedReportInfo = allAnalyticsReports.find(
              (r) => r._id === selectedReport,
            );

            // Sort data if a column is selected
            const sortedData = [...reportData].sort((a, b) => {
              if (!reportSortColumn) return 0;

              const aVal = a[reportSortColumn];
              const bVal = b[reportSortColumn];

              // Handle numeric values
              const aNum = parseFloat(String(aVal).replace(/[^0-9.-]/g, ""));
              const bNum = parseFloat(String(bVal).replace(/[^0-9.-]/g, ""));

              if (!isNaN(aNum) && !isNaN(bNum)) {
                return reportSortDirection === "asc"
                  ? aNum - bNum
                  : bNum - aNum;
              }

              // Handle string values
              const aStr = String(aVal).toLowerCase();
              const bStr = String(bVal).toLowerCase();

              if (reportSortDirection === "asc") {
                return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
              } else {
                return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
              }
            });

            const handleSort = (column) => {
              if (reportSortColumn === column) {
                setReportSortDirection(
                  reportSortDirection === "asc" ? "desc" : "asc",
                );
              } else {
                setReportSortColumn(column);
                setReportSortDirection("asc");
              }
            };

            return (
              <div
                className={`flex-1 flex flex-col overflow-hidden ${
                  isDarkMode ? "bg-gray-900" : "bg-gray-50"
                }`}
              >
                {/* Header with back button */}
                <div
                  className={`px-6 py-4 border-b ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setSelectedReport(null);
                          setReportSortColumn(null);
                          setReportSortDirection("asc");
                        }}
                        className={`mb-4 ${
                          isDarkMode
                            ? "text-gray-300 hover:text-white hover:bg-gray-700"
                            : ""
                        }`}
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Reports
                      </Button>
                      <h1 className={`text-2xl font-bold ${textClass}`}>
                        {selectedReportInfo?.title || "Report"}
                      </h1>
                      <p className={`text-sm mt-1 ${mutedTextClass}`}>
                        {selectedReportInfo?.description || "Report details"}
                      </p>
                      <p className={`text-xs mt-2 ${mutedTextClass}`}>
                        {filterType === "range" && dateFrom && dateTo && (
                          <>
                            Date Range: {format(dateFrom, "PP")} –{" "}
                            {format(dateTo, "PP")}
                          </>
                        )}

                        {filterType === "range" && (!dateFrom || !dateTo) && (
                          <>Select a date range</>
                        )}

                        {filterType === "month" &&
                          selectedMonth &&
                          selectedYear && (
                            <>
                              Showing{" "}
                              {format(
                                new Date(
                                  parseInt(selectedYear),
                                  parseInt(selectedMonth),
                                ),
                                "MMMM yyyy",
                              )}
                            </>
                          )}

                        {filterType === "all" && <>Showing all time</>}
                      </p>
                    </div>
                    <Button
                      onClick={() =>
                        exportToCSV(
                          selectedReport,
                          selectedReportInfo?.title || "report",
                        )
                      }
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={reportData.length === 0}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>

                {/* Report Data Table */}
                <div className="flex-1 overflow-y-auto p-6">
                  <Card
                    className={`${cardBgClass} border ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    }`}
                  >
                    <CardContent className="p-0">
                      {isAnalyticsLoading ? (
                        <div className="p-4">
                          <Stack spacing={1.5}>
                            {[...Array(10)].map((_, idx) => (
                              <Skeleton
                                key={idx}
                                variant="rectangular"
                                width="100%"
                                height={52}
                                sx={{
                                  bgcolor: isDarkMode
                                    ? "rgba(255, 255, 255, 0.05)"
                                    : "rgba(0, 0, 0, 0.06)",
                                  borderRadius: "4px",
                                }}
                              />
                            ))}
                          </Stack>
                        </div>
                      ) : reportData.length === 0 ? (
                        <div className={`p-8 text-center ${mutedTextClass}`}>
                          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No data available for this report</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={`border-b ${tableRowClass}`}>
                                {headers.map((header) => (
                                  <th
                                    key={header}
                                    onClick={() => handleSort(header)}
                                    className={`text-left py-3 px-4 font-medium ${labelClass} capitalize cursor-pointer hover:bg-opacity-50 ${
                                      isDarkMode
                                        ? "hover:bg-gray-700"
                                        : "hover:bg-gray-100"
                                    } transition-colors`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {header}
                                      <ArrowUpDown className="w-3 h-3 opacity-50" />
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sortedData.map((row, idx) => (
                                <tr
                                  key={idx}
                                  className={`border-b ${tableRowClass} ${rowHoverClass} transition-colors`}
                                >
                                  {headers.map((header, cellIdx) => (
                                    <td
                                      key={cellIdx}
                                      className={`py-3 px-4 ${
                                        cellIdx === 0
                                          ? `${textClass} font-medium`
                                          : mutedTextClass
                                      }`}
                                    >
                                      {renderCellValue(row[header])}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
};

export default Analytics;
